import { useState } from 'react';
import { MapContainer, TileLayer, Polyline } from 'react-leaflet';
import {
  Activity, AlertTriangle, FileSpreadsheet, Shield, Users, FileText,
  Upload, Check, Loader2, CalendarDays
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import PKMarkers from './PKMarkers';
import SlowDownManager from './SlowDownManager';
import MissionManager from './MissionManager';
import ConductorLeavesManager from './ConductorLeavesManager';
import LTVMapLayer from './LTVMapLayer';
import StationsMapLayer from './StationsMapLayer';
import IncidentsLayer from './IncidentsLayer';
import TrainPositionsLayer from './TrainPositionsLayer';
import SecurityRegisterView from './SecurityRegisterView';
import BulletinManager from './BulletinManager';
import AdminUserManagement from './AdminUserManagement';
import { Mission } from '../data/missions';
import { localApi, API_BASE_URL } from '../utils/localApi';

const PowerIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

interface TractionOperatorViewProps {
  displayName: string;
  onLogout: () => Promise<void>;
  currentTime: Date;
  isMobile: boolean;
  mapStyle: 'osm' | 'satellite';
  pathBalagne: [number, number][];
  pathCentrale: [number, number][];
  ltvList: any[];
  incidents: any[];
  missions: Mission[];
  fetchLTVs: () => Promise<void>;
  handleAddMission: (mission: Mission) => Promise<void>;
  handleAddMultipleMissions: (missions: Mission[]) => Promise<void>;
  handleUpdateMission: (mission: Mission) => Promise<void>;
  handleDeleteMission: (id: string) => Promise<void>;
  unreadSecuCount: number;
  session: any;
}

export default function TractionOperatorView({
  displayName,
  onLogout,
  currentTime,
  isMobile,
  mapStyle,
  pathBalagne,
  pathCentrale,
  ltvList,
  incidents,
  missions,
  fetchLTVs,
  handleAddMission,
  handleAddMultipleMissions,
  handleUpdateMission,
  handleDeleteMission,
  unreadSecuCount,
  session
}: TractionOperatorViewProps) {
  const [operatorTab, setOperatorTab] = useState<'live' | 'ltv' | 'missions' | 'operations' | 'security_docs' | 'users' | 'planning' | 'leaves'>('live');
  const [ltvLine, setLtvLine] = useState<'Centrale' | 'Balagne'>('Centrale');

  // Etats d'envoi du planning
  const [planningUploading, setPlanningUploading] = useState<Record<string, boolean>>({});
  const [planningError, setPlanningError] = useState<Record<string, string | null>>({});
  const [planningSuccess, setPlanningSuccess] = useState<Record<string, string | null>>({});

  const handleUploadPlanning = async (file: File, zone: string) => {
    const zoneKey = zone.toLowerCase();
    setPlanningUploading(prev => ({ ...prev, [zoneKey]: true }));
    setPlanningError(prev => ({ ...prev, [zoneKey]: null }));
    setPlanningSuccess(prev => ({ ...prev, [zoneKey]: null }));

    try {
      if (file.type !== 'application/pdf') {
        throw new Error('Seuls les fichiers PDF sont autorisés.');
      }

      const res = await localApi.uploadPlanning(file, zoneKey);
      if (res.error) throw new Error(res.error);

      setPlanningSuccess(prev => ({ ...prev, [zoneKey]: 'Planning mis à jour avec succès !' }));
    } catch (err: any) {
      setPlanningError(prev => ({ ...prev, [zoneKey]: err.message || 'Erreur lors du téléversement.' }));
    } finally {
      setPlanningUploading(prev => ({ ...prev, [zoneKey]: false }));
    }
  };

  return (
    <div className="h-screen bg-[#020617] flex flex-col overflow-hidden text-slate-300 font-sans">
      {/* HEADER OPÉRATEUR TRACTION */}
      <header className={`px-4 pt-[env(safe-area-inset-top)] border-b border-white/5 bg-slate-900/90 backdrop-blur-md flex justify-between items-center z-[1000] shadow-xl ${isMobile ? 'h-[calc(4rem+env(safe-area-inset-top))]' : 'h-20'}`}>
        {/* GAUCHE : Logo & Profil */}
        <div className="flex-1 flex items-center gap-2 sm:gap-4 overflow-hidden">
          <img src="logo.png" alt="mini logo" className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} object-contain`} />
          {!isMobile && <div className="h-6 w-[1px] bg-white/10"></div>}
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center gap-2">
              <h2 className="text-white font-black italic text-[10px] sm:text-xs tracking-tighter uppercase truncate leading-none">
                OPÉRATEUR TRACTION • {displayName}
              </h2>
              <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[8px] font-black uppercase rounded border border-emerald-500/30 animate-pulse">
                TRACTION LIVE
              </span>
            </div>
            <p className="text-[9px] font-mono text-cyan-400 truncate tracking-tight mt-0.5">
              Supervision de la traction & sécurité opérationnelle
            </p>
          </div>
        </div>

        {/* CENTRE : Date & Heure */}
        {!isMobile && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] font-black text-cyan-500 uppercase tracking-widest leading-none mb-1">
              {currentTime.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
            <p className="text-xl font-mono font-bold text-white leading-none">
              {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          </div>
        )}

        {/* DROITE : Statut et Déconnexion */}
        <div className="flex-1 flex items-center justify-end gap-2 sm:gap-6">
          {!isMobile && (
            <div className="hidden sm:flex items-center gap-2 border-l border-white/10 pl-6 pr-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-[10px] font-mono text-slate-400 uppercase">Traction Network Online</span>
            </div>
          )}
          <button 
            onClick={onLogout} 
            className="flex items-center justify-center p-2 hover:bg-red-500/10 rounded-xl transition-all active:scale-95 group"
            title="Se déconnecter"
          >
            <PowerIcon className="w-7 h-7 text-red-500 group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(239,68,68,0.4)]" />
          </button>
        </div>
      </header>

      {/* CONTENU PRINCIPAL AVEC SIDEBAR */}
      <div className="flex-1 flex overflow-hidden bg-[#0a0f1c]">
        {/* SIDEBAR NAVIGATION */}
        <div className="w-[80px] lg:w-[280px] shrink-0 bg-[#0f172a] border-r border-white/10 flex flex-col z-10 shadow-2xl transition-all duration-300">
          <div className="p-4 lg:p-6 border-b border-white/10 flex flex-col items-center lg:items-start">
            <h2 className="text-white font-black uppercase tracking-widest text-[10px] lg:text-lg text-center lg:text-left">
              <span className="lg:hidden">TRACTION</span>
              <span className="hidden lg:inline">Poste Traction</span>
            </h2>
            <p className="text-emerald-400 text-[9px] font-mono mt-1 pr-4 hidden lg:block">Modules Opérationnels</p>
          </div>

          <nav className="flex-1 overflow-y-auto p-2 lg:p-4 flex flex-col gap-2">
            <button
              onClick={() => setOperatorTab('live')}
              title="Suivi Trafic Live"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${operatorTab === 'live' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'live' ? 'bg-blue-500/20 border-blue-500/30' : 'bg-slate-800 border-white/5'}`}>
                <Activity size={16} className={operatorTab === 'live' ? 'animate-pulse' : ''} />
              </div>
              <span className="hidden lg:block text-left">Suivi Trafic Live</span>
            </button>

            <button
              onClick={() => setOperatorTab('ltv')}
              title="ASS"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${operatorTab === 'ltv' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'ltv' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                <AlertTriangle size={16} />
              </div>
              <span className="hidden lg:block text-left">ASS</span>
            </button>

            <button
              onClick={() => setOperatorTab('missions')}
              title="Plan de Transport (Missions)"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${operatorTab === 'missions' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'missions' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                <FileSpreadsheet size={16} />
              </div>
              <span className="hidden lg:block text-left">Missions</span>
            </button>

            <button
              onClick={() => setOperatorTab('operations')}
              title="Suivi des Opérations"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${operatorTab === 'operations' ? 'bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'operations' ? 'bg-red-500/20 border-red-500/30' : 'bg-slate-800 border-white/5'}`}>
                <Shield size={16} />
              </div>
              <span className="hidden lg:block text-left">Suivi des Opérations</span>
            </button>

            <button
              onClick={() => setOperatorTab('security_docs')}
              title="Documents de Sécurité"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs relative ${operatorTab === 'security_docs' ? 'bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'security_docs' ? 'bg-red-500/20 border-red-500/30' : 'bg-slate-800 border-white/5'}`}>
                <FileText size={16} />
              </div>
              <span className="hidden lg:block text-left">Documents de Sécurité</span>
              {unreadSecuCount > 0 && (
                <span className="absolute top-2 right-2 lg:static lg:ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]" />
              )}
            </button>

            <button
              onClick={() => setOperatorTab('users')}
              title="Gestion des Agents"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${operatorTab === 'users' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'users' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                <Users size={16} />
              </div>
              <span className="hidden lg:block text-left">Gestion des Agents</span>
            </button>

            <button
              onClick={() => setOperatorTab('planning')}
              title="Gestion des Plannings"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${operatorTab === 'planning' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'planning' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                <FileText size={16} />
              </div>
              <span className="hidden lg:block text-left">Gestion Plannings</span>
            </button>

            <button
              onClick={() => setOperatorTab('leaves')}
              title="Congés Conducteurs"
              className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${operatorTab === 'leaves' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${operatorTab === 'leaves' ? 'bg-amber-500/20 border-amber-500/30' : 'bg-slate-800 border-white/5'}`}>
                <CalendarDays size={16} />
              </div>
              <span className="hidden lg:block text-left">Congés</span>
            </button>
          </nav>
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 relative overflow-auto bg-[#0a0f1c]">
          <div className="min-h-full p-6 sm:p-8">
            {/* ONGLET LTV */}
            {operatorTab === 'ltv' && (
              <div className="max-w-7xl mx-auto space-y-6">
                <div className="flex bg-slate-800/60 p-1.5 rounded-2xl gap-1 border border-white/5 max-w-md mb-8 shadow-lg">
                  <button
                    onClick={() => setLtvLine('Centrale')}
                    className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                      ltvLine === 'Centrale'
                        ? 'bg-cyan-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.4)] border border-cyan-400/50'
                        : 'text-slate-500 hover:text-white'
                    }`}
                  >
                    Ligne Centrale
                  </button>
                  <button
                    onClick={() => setLtvLine('Balagne')}
                    className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                      ltvLine === 'Balagne'
                        ? 'bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.4)] border border-red-400/50'
                        : 'text-slate-500 hover:text-white'
                    }`}
                  >
                    Ligne Balagne
                  </button>
                </div>
                <SlowDownManager
                  line={ltvLine}
                  ltvList={ltvList.filter(ltv =>
                    ltvLine === 'Centrale'
                      ? ltv.line === 'Centrale' || !ltv.line
                      : ltv.line === 'Balagne'
                  )}
                  onRefresh={fetchLTVs}
                />
              </div>
            )}

            {/* ONGLET MISSIONS */}
            {operatorTab === 'missions' && (
              <div className="max-w-7xl mx-auto bg-slate-900/40 rounded-3xl border border-white/5 p-6 backdrop-blur-sm">
                <MissionManager
                  missions={missions.filter(m => !m.id.startsWith('permanent-'))}
                  onAdd={handleAddMission}
                  onAddMultiple={handleAddMultipleMissions}
                  onUpdate={handleUpdateMission}
                  onDelete={handleDeleteMission}
                />
              </div>
            )}

            {/* ONGLET AGENTS */}
            {operatorTab === 'users' && <AdminUserManagement />}

            {/* ONGLET CONGÉS */}
            {operatorTab === 'leaves' && <ConductorLeavesManager />}

            {/* ONGLET PLANNINGS */}
            {operatorTab === 'planning' && (
              <div className="max-w-7xl mx-auto space-y-6">
                <div className="bg-slate-900/40 rounded-3xl border border-white/5 p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                      <FileText className="text-cyan-400" size={24} />
                    </div>
                    <div>
                      <h3 className="text-white font-black uppercase tracking-widest text-base">Gestion des Plannings de Service</h3>
                      <p className="text-slate-400 text-xs mt-0.5 font-bold uppercase tracking-wider">Téléversement et consultation en direct des plannings par zone.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {['Bastia', 'Ajaccio', 'Calvi'].map(zone => {
                      const zoneKey = zone.toLowerCase();
                      const isUploading = !!planningUploading[zoneKey];
                      const error = planningError[zoneKey];
                      const success = planningSuccess[zoneKey];

                      return (
                        <div key={zone} className="bg-slate-800/40 border border-white/5 p-6 rounded-2xl flex flex-col justify-between hover:border-cyan-500/30 transition-all">
                          <div>
                            <h4 className="text-white font-bold text-lg mb-2">{zone}</h4>
                            <p className="text-xs text-slate-500 mb-6">
                              Glissez-déposez ou sélectionnez le planning PDF officiel de la zone {zone} pour écraser la version en vigueur.
                            </p>

                            {/* DRAG AND DROP ZONE */}
                            <label
                              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const file = e.dataTransfer.files?.[0];
                                if (file) {
                                  handleUploadPlanning(file, zone);
                                }
                              }}
                              className="relative group cursor-pointer block border-2 border-dashed border-white/10 hover:border-cyan-500/50 rounded-xl p-6 text-center transition-all bg-black/20 mb-4"
                            >
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadPlanning(file, zone);
                                }}
                                disabled={isUploading}
                                className="hidden"
                              />
                              <div className="space-y-2">
                                {isUploading ? (
                                  <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
                                ) : (
                                  <Upload className="w-8 h-8 text-slate-600 group-hover:text-cyan-500 transition-all mx-auto" />
                                )}
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                  {isUploading ? 'Téléversement...' : 'Déposer ou Choisir PDF'}
                                </p>
                              </div>
                            </label>

                            {error && (
                              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-500 text-[9px] font-bold uppercase tracking-widest">
                                <AlertTriangle size={14} className="shrink-0" />
                                <span>{error}</span>
                              </div>
                            )}

                            {success && (
                              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-2 text-green-400 text-[9px] font-bold uppercase tracking-widest">
                                <Check size={14} className="shrink-0" />
                                <span>{success}</span>
                              </div>
                            )}
                          </div>

                          <div className="border-t border-white/5 pt-4 mt-2">
                            <a
                              href={`${API_BASE_URL}/download.php?file=planning_${zoneKey}&token=${session?.access_token || localStorage.getItem('nexus_token')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] font-black uppercase tracking-widest text-cyan-400 hover:text-cyan-300 flex items-center justify-center gap-1.5 transition-colors"
                            >
                              <FileText size={12} /> Consulter le planning actuel
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET SUIVI DES OPÉRATIONS (BOÎTE NOIRE) */}
            {operatorTab === 'operations' && (
              <div className="max-w-7xl mx-auto space-y-6">
                <div className="bg-slate-900/60 rounded-3xl border border-white/5 p-1 sm:p-6 backdrop-blur-md">
                  <SecurityRegisterView />
                </div>
              </div>
            )}

            {/* ONGLET DOCUMENTS DE SÉCURITÉ (GESTION PDF) */}
            {operatorTab === 'security_docs' && (
              <div className="max-w-7xl mx-auto space-y-6">
                <div className="bg-slate-900/60 rounded-3xl border border-white/5 p-1 sm:p-6 backdrop-blur-md">
                  <BulletinManager />
                </div>
              </div>
            )}

            {/* ONGLET LIVE */}
            {operatorTab === 'live' && (
              <div className="h-[calc(100vh-120px)] w-full rounded-3xl overflow-hidden border border-white/10 shadow-2xl relative">
                <MapContainer
                  center={[42.45, 9.15]}
                  zoom={10}
                  style={{ height: '100%', width: '100%' }}
                  className="admin-map"
                >
                  {mapStyle === 'satellite' ? (
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                  ) : (
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                  )}
                  <Polyline positions={pathBalagne} color="#22d3ee" weight={4} opacity={0.4} />
                  <Polyline positions={pathCentrale} color="#a855f7" weight={4} opacity={0.4} />
                  <PKMarkers />
                  <StationsMapLayer />
                  <LTVMapLayer ltvList={ltvList} />
                  <IncidentsLayer incidents={incidents} />
                  <TrainPositionsLayer />
                </MapContainer>

                {/* Overlay Legend */}
                <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-md p-4 rounded-2xl border border-white/10 z-[1000] pointer-events-none">
                  <h3 className="text-[10px] font-black uppercase text-cyan-500 tracking-widest mb-3">Légende Live</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-slate-900 border border-blue-400 rounded-full shadow-[0_0_5px_rgba(59,130,246,0.5)]"></div>
                      <span className="text-[9px] font-bold text-white uppercase">Trains en circulation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-[9px] font-bold text-slate-300 uppercase">Limitations de vitesse</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-1 bg-cyan-400 rounded-full"></div>
                      <span className="text-[9px] font-bold text-slate-300 uppercase">Réseau Ferrer</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
